import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Backdrop,
  Box,
  Chip,
  Link,
  Pagination,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../constants";
import InvoiceClonePopUp from "../../modules/PopUp/InvoiceClonePopUp";
import SelectedParticipantListPopUp from "../../modules/PopUp/SelectedParticipantListPopUp";
import { getClientDetailsByClientName } from "../../modules/fiscalUpgrade/Utils/ClientSettingsTable";
import CommonUtilities from "../../modules/fiscalUpgrade/Utils/CommonUtilities";
import { getInvoiceDetailsAndNavigate } from "../../modules/fiscalUpgrade/services/Invoices/Project/invoiceUtils";
import { RootState } from "../../store/store";
import ContainedButton from "../Button/CustomContainedButton";
import "./ReusableTable.css";
import TableRowStyled from "./TableRowStyled";

export interface Column {
  isHyperlink?: boolean;
  id: string;
  label: string;
  minWidth?: any;
  align?: "right" | "left" | "center";
  format?: (value: any) => string;
}

interface ReusableTableProps {
  tableKey?: string;
  columns: Column[];
  rows: Record<string, any>[];
  count?: number; // made optional
  participantType?: string;
  page?: number; // made optional
  rowsPerPage?: number; // made optional
  onPageChange?: (newPage: number, tableKey?: string) => void; // made optional
  onRowsPerPageChange?: (newRowsPerPage: number, tableKey?: string) => void; // made optional
  onDelete?: (row: Record<string, any>, tableKey?: string) => void;
  onRowClick?: (row: Record<string, any>, tableKey?: string) => void;
  onCheckboxChange?: (
    row: Record<string, any>,
    checked: boolean,
    tableKey?: string
  ) => void;
  loading?: boolean; // made optional
  containerClass?: string;
  defaultParticipantId?: number;
  setSelectedRowsData?: (selectedRowsData: Record<string, boolean>) => void;
  checkedData?: any;
  vendorMessage?: string;
  cellStyle?: (col: any) => React.CSSProperties;
}

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  "& .MuiTableCell-root": {
    border: "0.5px solid #f2f6fa",
  },
  borderBottom: "1px solid #dadcdc",
}));

const ReusableTable: React.FC<ReusableTableProps> = ({
  tableKey,
  columns,
  rows,
  count,
  participantType,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onDelete,
  onRowClick,
  onCheckboxChange,
  loading = false,
  defaultParticipantId,
  containerClass = "container",
  setSelectedRowsData,
  checkedData,
  vendorMessage,
}) => {
  const [selectedRowsData, setLocalSelectedRowsData] = useState<
    Record<string, boolean>
  >({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const contactInformation = useSelector(
    (state: RootState) => state.participantAdminProfile
  );
  const [userRole] = useState<string>(
    contactInformation.participantAdminProfile
  );
  const userInfo = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);
  const [blackdropFlag, setBlackdropFlag] = useState(false);
  const [openCancelPopUp, setOpenCancelPopUp] = useState(false);
  const [invoiceCloneDetails, setInvoiceCloneDetails] = useState<any>();
  const [popperAnchorEl, setPopperAnchorEl] = useState<HTMLElement | null>(null);
  const [openPopperRowIndex, setOpenPopperRowIndex] = useState<number | null>(null);

  useEffect(() => {
    if (checkedData) {
      setLocalSelectedRowsData(checkedData);
    }
  }, [checkedData]);
  const handleCheckboxChange = (rowId: string, checked: boolean) => {
    const updatedRows = { ...selectedRowsData, [rowId]: checked };
    if (!checked) {
      delete updatedRows[rowId]; // Remove the row from selected rows if unchecked
    }

    setLocalSelectedRowsData(updatedRows); // Update local state
    if (setSelectedRowsData) {
      setSelectedRowsData(updatedRows);
    }
  };
  const handleCancelPopUpClose = () => {
    setOpenCancelPopUp(false);
  };
  return (
    <div>
      <>
        <TableContainer
          sx={{
            overflow: "auto",
            maxHeight: "60vh",
            maxWidth: tableKey === "vendorUploadDocuments" ? "100%" : "auto",
            height:
              tableKey === "VendorList"
                ? "50vh"
                : tableKey === "SelectApproverList"
                  ? "33vh"
                  : "auto",
            width:
              tableKey === "VendorList" || tableKey === "SelectApproverList"
                ? "100vh"
                : "auto",
          }}
        >
          <StyledTable
            stickyHeader
            aria-label="sticky table"
            size="small"
            sx={{
              [`& .${tableCellClasses.root}`]: {
                borderTop: "none",
              },
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column: any) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    className="cellTask"
                    style={{
                      width: column?.minWidth ? column.minWidth : "auto",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                {onDelete && (
                  <TableCell className="cellTask" align="center">
                    {translate("action")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Show loading spinner inside a TableRow
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (onDelete ? 1 : 0)}
                    align="center"
                    style={{
                      height:
                        tableKey === "VendorList" ||
                          tableKey === "SelectApproverList"
                          ? "40vh"
                          : "auto",
                    }}
                  >
                    <div >
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <span style={{ fontSize: "20px" }}>Loading...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : rows?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns?.length + (onDelete ? 1 : 0)}
                    align="center"
                    style={{
                      height: tableKey === "VendorList" ? "40vh" : "auto",
                    }}

                  >
                    {tableKey === "VendorList"
                      ? vendorMessage
                      : translate("noRecordsFound")}
                  </TableCell>
                </TableRow>
              ) : (
                rows?.map((row, index) => (
                  <TableRowStyled
                    key={index}
                    hover={
                      tableKey === "VendorList" ||
                      tableKey === "SelectApproverList"
                    }
                    onClick={() => {
                      if (
                        (tableKey === "VendorList" ||
                          tableKey === "SelectApproverList") &&
                        onRowClick
                      ) {
                        onRowClick(row, tableKey);
                      }
                    }}
                    style={{
                      cursor:
                        tableKey === "VendorList" ||
                          tableKey === "SelectApproverList"
                          ? "pointer"
                          : "default",
                      ...((tableKey === "VendorList" ||
                        tableKey === "SelectApproverList") && {
                        "&:hover": {
                          backgroundColor: "#EAF4F6",
                        },
                      }),
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        className="left-aligned-text"
                        style={{
                          width: column?.minWidth ? column.minWidth : "auto",

                        }}
                      >
                        {column.label === "" ? (
                          <Checkbox
                            checked={!!selectedRowsData[row.id]} // Check by unique ID
                            onChange={(e) =>
                              handleCheckboxChange(row.id, e.target.checked)
                            }
                          />
                        ) :
                          column.id === "description" || column.id === "userComments" ? (
                            <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                              {row[column.id]}
                            </div>
                          ) : column.id === "budgetReport" ||
                            column.id === "financialReport" ? (
                            (
                              column.id === "financialReport"
                                ? row.forecastYear &&
                                ((participantType === "School" &&
                                  row.forecastYear > "FY17") ||
                                  (participantType !== "School" &&
                                    row.forecastYear > "FY18"))
                                : row.forecastYear && row.forecastYear > "FY18"
                            ) ? (
                              <a
                                href="#"
                                className="hoverLink"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (column.id === "budgetReport") {
                                    navigate("/budgetReportView", {
                                      state: { rowData: row },
                                    });
                                  } else if (column.id === "financialReport") {
                                    navigate("/actualsReportView", {
                                      state: { rowData: row },
                                    });
                                  }
                                }}
                              >
                                View
                              </a>
                            ) : (
                              "-"
                            )
                          ) : column.id === "RCBOInvoicepayment" ? (
                            userRole === constants.ADMIN_VIEW || row.status === "Paid" ? (
                              <span>{row.RCBOInvoicepayment}</span>
                            ) : (
                              <a
                                href="#"
                                className="hoverLink"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate("/rcboInvoicePayment", {
                                    state: { rowData: row },
                                  });
                                }}
                              >
                                {row.RCBOInvoicepayment}
                              </a>
                            )
                          ) : column.id === "transactionDate" && tableKey === "blackBaudTransactionLog" ? (
                            <Link
                              className="hoverLink"
                              sx={{ cursor: "pointer", color: "#1976d2" }}
                              onClick={() => navigate("/blackBaud-Transaction-batch-details", {
                                state: {
                                  rowData: row.tranDate,
                                },
                              })}
                            >
                              {row.transactionDate}
                            </Link>
                          ) : column.id === "creditPayment" ? (
                            row.creditPayment != translate('makePayment') && row.creditPayment != translate('paymentWithdrawal') ? (
                              <span>{row.creditPayment}</span>
                            ) : (
                              <a
                                href="#"
                                className="hoverLink"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(ClientDetails?.allowedPaymentTypes === constants.ALLOWED_PAYMENT_TYPES_NON_DNL ? "/creditLinePayments" : "/creditLineMakePayment",
                                    {
                                      state: { rowData: row },
                                    });
                                }}
                              >
                                {row.creditPayment}
                              </a>
                            )
                          ) : column.id === "status" &&
                            tableKey === "projectInvoice" ? (
                            <Stack direction="column" spacing={1}>
                              <a
                                href="#"
                                className="hoverLink"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  if (row.status === "Draft") {
                                    try {
                                      setBlackdropFlag(true);
                                      await getInvoiceDetailsAndNavigate(
                                        row.invoiceId,
                                        row.clientId,
                                        dispatch,
                                        navigate
                                      );
                                    } finally {
                                      setBlackdropFlag(false);
                                    }
                                  } else {
                                    const cleanRow = {
                                      ...row,
                                      description:
                                        typeof row.description === "object" && row.description?.props
                                          ? row.description.props.displayText || ""
                                          : row.description,
                                    };

                                    navigate("/vendorInvoiceDetails", {
                                      state: {
                                        rowData: cleanRow,
                                        fromPage: "projectInvoice",
                                      },
                                    });

                                  }
                                }}
                              >
                                {[
                                  "PM Rejected",
                                  "BM Rejected",
                                  "CM Rejected",
                                ].includes(row.status?.trim())
                                  ? "Rejected"
                                  : row.status?.trim().startsWith("Waiting")
                                    ? "Pending Site Approval"
                                    : row.status?.trim()}
                              </a>

                              {row.status === "Cancelled" &&
                                [constants.ROLES_BUDGET_ANALYST, constants.ROLES_ADMIN_USER].includes(userInfo?.roles?.roleName ?? "")
                                && (
                                  <div
                                    style={{
                                      width: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <ContainedButton
                                      variant="contained"
                                      size="small"
                                      style={{
                                        width: "50%",
                                        textAlign: "center",
                                      }}
                                      onClick={() => {
                                        setInvoiceCloneDetails(row);
                                        setOpenCancelPopUp(true);
                                      }}
                                    >
                                      Clone
                                    </ContainedButton>
                                  </div>
                                )}
                            </Stack>
                          ) : column.id === "invoiceNumber" &&
                            tableKey === "invoiceTasks" ? (
                            <a
                              href="#"
                              className="hoverLink"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate("/vendorInvoiceDetails", {
                                  state: {
                                    rowData: row,
                                    fromPage: "projectTaskInvoice",
                                  },
                                });
                              }}
                            >
                              {row.invoiceNumber}
                            </a>
                          ) : column.id === "participants" && tableKey === "userListing" ? (
                            (() => {
                              const MAX_LENGTH = 25;
                              const isAllParticipants = row.participants.includes("All Participants");
                              const joinedParticipants = row.participants.includes("All Participants")
                                ? "All Participants"
                                : row.participants.join(", ");
                              const showMore = isAllParticipants || joinedParticipants.length > MAX_LENGTH;
                              const displayText =
                                showMore && !isAllParticipants
                                  ? joinedParticipants.slice(0, MAX_LENGTH - 3) + "..."
                                  : joinedParticipants;

                              return (
                                <>
                                  {displayText}
                                  {showMore && (
                                    <Chip
                                      label="More"
                                      clickable
                                      onClick={(e) => {
                                        setPopperAnchorEl(e.currentTarget);
                                        setOpenPopperRowIndex(index);
                                      }}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      style={{ marginLeft: 8 }}
                                    />
                                  )}
                                </>
                              );
                            })()
                          ) : column.isHyperlink ? (
                            <a
                              href="#"
                              className="hoverLink"
                              onClick={(e) => {
                                e.preventDefault();
                                if (onRowClick) {
                                  onRowClick(row, tableKey);
                                }
                              }}
                            >
                              {row[column.id]}
                            </a>
                          ) : (
                            row[column.id]
                          )}
                      </TableCell>
                    ))}

                    {onDelete && (
                      <TableCell align="center">
                        <DeleteIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              tableKey === "selectedParticipant" &&
                              defaultParticipantId === row.participantId
                            ) {
                              return;
                            }
                            onDelete?.(row, tableKey);
                          }}
                          style={{
                            cursor:
                              tableKey === "selectedParticipant" &&
                                defaultParticipantId === row.participantId
                                ? "not-allowed"
                                : "pointer",
                            color:
                              tableKey === "selectedParticipant" &&
                                defaultParticipantId === row.participantId
                                ? "gray"
                                : "inherit",
                          }}
                        />
                      </TableCell>
                    )}
                  </TableRowStyled>
                ))
              )}
            </TableBody>
          </StyledTable>
        </TableContainer>
        {/* Render the Popper here, after the table */}
        {openPopperRowIndex !== null && (
          // <ParticipantPopper
          //   anchorEl={popperAnchorEl}
          //   open={Boolean(popperAnchorEl)}
          //   onClose={() => {
          //     setPopperAnchorEl(null);
          //     setOpenPopperRowIndex(null);
          //   }}
          //   participants={rows[openPopperRowIndex].participants}
          // /> 
          <SelectedParticipantListPopUp
            open={Boolean(popperAnchorEl)}
            handleClose={() => {
              setPopperAnchorEl(null);
              setOpenPopperRowIndex(null);
            }}
            participantList={rows[openPopperRowIndex].participants}
          />
        )}
        {typeof count === "number" && count > 4 && !loading && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ width: "100%", padding: "10px", marginBottom: "5px" }}
          >
            {/* Left Corner: Showing total entries */}
            <Typography variant="body2" sx={{ fontSize: "18px" }}>
              {translate("showing")}{" "}
              {`${(page ?? 0) * (rowsPerPage ?? 0) + 1}-${Math.min(
                ((page ?? 0) + 1) * (rowsPerPage ?? 0),
                count
              )} ${translate("of")} ${count} ${translate("entries")}`}
            </Typography>

            {/* Center: Rows Per Page Dropdown */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="body2"
                sx={{ marginRight: 1, fontSize: "18px" }}
              >
                {translate("show")}:
              </Typography>
              <Autocomplete
                sx={{ width: 70, fontSize: "18px" }}
                size="small"
                disableClearable
                options={CommonUtilities.paginationOptions ?? []}
                getOptionLabel={(option) => option.toString()}
                value={rowsPerPage ?? 10}
                onChange={(event, newValue) => {
                  if (newValue !== null && onRowsPerPageChange) {
                    onRowsPerPageChange(newValue, tableKey);
                    onPageChange?.(0, tableKey);
                    setPopperAnchorEl(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />

              <Typography variant="body2" sx={{ fontSize: "18px" }} ml={1}>
                {translate("entries")}
              </Typography>
            </Box>

            {/* Right Corner: Pagination Controls */}
            <Pagination
              count={Math.ceil(count / (rowsPerPage ?? 1))}
              page={(page ?? 0) + 1}
              onChange={(e, newPage) => {
                onPageChange?.(newPage - 1, tableKey)
                setPopperAnchorEl(null);
              }}
              showFirstButton
              showLastButton
            />
          </Stack>
        )}
        <InvoiceClonePopUp
          open={openCancelPopUp}
          handleClose={handleCancelPopUpClose}
          invoiceCloneDetails={invoiceCloneDetails}
        />
        <Backdrop
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 1,
            display: "flex",
            flexDirection: "column",
          })}
          open={blackdropFlag}
        >
          <CircularProgress color="inherit" />
          <Typography color="white">Please wait...</Typography>
        </Backdrop>
      </>
    </div>
  );
};

export default ReusableTable;
